import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useGreeting } from "../lib/greeting";

const REDUCED =
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

// Molaroo branded opening experience. Mascot/logo hero + personalised, device-local
// greeting. Shown once per genuine app launch (mounts with App), fades out ~1.8s after
// auth is ready (longer, premium hold vs Build 2). Respects reduced-motion. It does NOT
// replay on tab change / modal / navigation because App does not remount.
export function BrandedSplash() {
  const { ready, user } = useAuth();
  const greeting = useGreeting(user); // device-local time; recomputes on resume/visibility
  const [hidden, setHidden] = useState(false);
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    if (!ready) return;
    const t = setTimeout(() => setHidden(true), REDUCED ? 700 : 1800);
    return () => clearTimeout(t);
  }, [ready]);

  useEffect(() => {
    if (!hidden) return;
    const t = setTimeout(() => setMounted(false), REDUCED ? 0 : 450);
    return () => clearTimeout(t);
  }, [hidden]);

  if (!mounted) return null;

  return (
    <div
      data-testid="branded-splash"
      aria-hidden={hidden}
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white ${
        REDUCED ? "" : "transition-opacity duration-[450ms]"
      } ${hidden ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
    >
      <img
        src="/molaroo-logo.png"
        alt="Molaroo — Your work. Your money. Under control."
        className={`w-72 max-w-[76vw] h-auto object-contain ${REDUCED ? "" : "df-fade-in"}`}
      />
      <p data-testid="splash-greeting" className="mt-6 text-base font-semibold text-slate-800 text-center px-6">
        {greeting}
      </p>
      {REDUCED ? null : (
        <div
          className="mt-6 h-6 w-6 rounded-full border-2 border-[#0D786A]/25 border-t-[#0D786A] animate-spin"
          aria-hidden="true"
        />
      )}
    </div>
  );
}
